import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ActivatedRoute, Router} from "@angular/router";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgbDateStruct, NgbInputDatepicker, NgbPagination} from "@ng-bootstrap/ng-bootstrap";
import {saveAs} from "file-saver";
import {Subscription} from "rxjs";
import {formatDate} from "@angular/common";
import {ApiService} from "@app/_services/api.service";
import {GlobalService} from "@app/_services/global.service";
import {ToastService} from "@app/_services/toast.service";
import {Customer, ProcessingRequestHistoryItem} from "@app/_models/api";
import {toDateStruct, toIsoDate} from "@app/_helpers/date-struct";
import {isControlInvalid} from "@app/_helpers/form-validation";
import {
    buildCsv,
    dayHeading,
    dayKey,
    defaultFilters,
    formatDateTime,
    isStuck,
    RequestHistoryFilters,
    sortNewestFirst,
    toRequestParams
} from "@app/processing/request-history/request-history.helpers";

type DayGroup = { key: string; heading: string; items: ProcessingRequestHistoryItem[] };

@Component({
    selector: "app-request-history",
    imports: [ReactiveFormsModule, NgbInputDatepicker, NgbPagination],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./request-history.component.html",
    styleUrl: "./request-history.component.scss"
})
export class RequestHistoryComponent {
    readonly pageSize = 25;
    readonly customers: Customer[];

    readonly form = new FormGroup({
        requestType: new FormControl("", {nonNullable: true}),
        fileType: new FormControl("", {nonNullable: true}),
        status: new FormControl("", {nonNullable: true}),
        customerId: new FormControl("", {nonNullable: true}),
        from: new FormControl<NgbDateStruct | null>(null),
        to: new FormControl<NgbDateStruct | null>(null),
        fileName: new FormControl("", {nonNullable: true}),
        requestedBy: new FormControl("", {nonNullable: true})
    });

    readonly items = signal<ProcessingRequestHistoryItem[]>([]);
    readonly page = signal(1);
    readonly fileGuid = signal("");
    readonly loadedAt = signal(new Date());
    readonly loadFailed = signal(false);

    readonly groups = computed<DayGroup[]>(() => {
        const start = (this.page() - 1) * this.pageSize;
        const groups: DayGroup[] = [];

        for (const item of this.items().slice(start, start + this.pageSize)) {
            const key = dayKey(item.createdAt);
            const group = groups.find(g => g.key === key);
            if (group) {
                group.items.push(item);
            } else {
                groups.push({key, heading: dayHeading(item.createdAt), items: [item]});
            }
        }

        return groups;
    });

    protected readonly formatDateTime = formatDateTime;
    protected readonly isControlInvalid = isControlInvalid;

    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly apiService = inject(ApiService);
    private readonly globalService = inject(GlobalService);
    private readonly toastService = inject(ToastService);
    private searchSubscription?: Subscription;

    constructor() {
        this.customers = [...this.route.snapshot.data["resolvedData"].data as Customer[]]
            .sort((a, b) => a.name.localeCompare(b.name));
        this.applyFilters(defaultFilters());

        inject(DestroyRef).onDestroy(() => this.searchSubscription?.unsubscribe());

        this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe(params => {
            this.fileGuid.set(params.get("fileGuid") ?? "");
            this.search();
        });
    }

    search(): void {
        if (!this.fileGuid() && (this.form.controls.from.invalid || this.form.controls.to.invalid)) {
            this.form.markAllAsTouched();
            return;
        }

        this.searchSubscription?.unsubscribe();
        this.items.set([]);
        this.page.set(1);
        this.loadFailed.set(false);
        this.globalService.showWaitOverlay("Loading requests...");

        this.searchSubscription = this.apiService.getProcessingRequests(toRequestParams(this.readFilters())).subscribe({
            next: (items) => {
                this.globalService.hideWaitOverlay();
                this.items.set(sortNewestFirst(items));
                this.loadedAt.set(new Date());
            },
            error: () => {
                this.globalService.hideWaitOverlay();
                this.loadFailed.set(true);
                this.toastService.showDanger("Request failed", "Failed to load the request history.");
            }
        });
    }

    reset(): void {
        this.applyFilters(defaultFilters());

        if (this.fileGuid()) {
            this.showAllRequests();
        } else {
            this.search();
        }
    }

    showAllRequests(): void {
        this.router.navigate([], {queryParams: {fileGuid: null}, queryParamsHandling: "merge"});
    }

    exportCsv(): void {
        const items = this.items();

        if (items.length === 0) {
            this.toastService.showInfo("Export CSV", "There are no requests matching the current filters to export.");
            return;
        }

        const timestamp = formatDate(new Date(), "yyyyMMdd-HHmm", "en-US");
        // Leading BOM so Excel on Windows opens the file as UTF-8 instead of the system codepage.
        saveAs(new Blob(["﻿" + buildCsv(items)], {type: "text/csv;charset=utf-8;"}),
            `processing-request-history-${timestamp}.csv`);
    }

    isStuck(item: ProcessingRequestHistoryItem): boolean {
        return isStuck(item, this.loadedAt());
    }

    private readFilters(): RequestHistoryFilters {
        const value = this.form.getRawValue();

        return {
            ...value,
            fileGuid: this.fileGuid(),
            from: toIsoDate(value.from),
            to: toIsoDate(value.to)
        };
    }

    private applyFilters(filters: RequestHistoryFilters): void {
        this.form.setValue({
            requestType: filters.requestType,
            fileType: filters.fileType,
            status: filters.status,
            customerId: filters.customerId,
            from: toDateStruct(filters.from),
            to: toDateStruct(filters.to),
            fileName: filters.fileName,
            requestedBy: filters.requestedBy
        });
    }
}
